import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Play, Loader2 } from 'lucide-react';
import { useWebhookConfigs } from '@/hooks/useWebhookConfigs';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function WebhookTester() {
  const { data: configs } = useWebhookConfigs();
  const [selectedWebhook, setSelectedWebhook] = useState<string>('');
  const [payload, setPayload] = useState<string>('{\n  \n}');
  const [isDryRun, setIsDryRun] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [resultStatus, setResultStatus] = useState<'success' | 'error' | null>(null);

  const handleSend = async () => {
    if (!selectedWebhook) {
      toast.error('Selecione um webhook');
      return;
    }

    let parsedPayload: any;
    try {
      parsedPayload = JSON.parse(payload);
    } catch {
      toast.error('JSON inválido');
      return;
    }

    setIsLoading(true);
    setResult(null);
    setResultStatus(null);

    try {
      const headers: Record<string, string> = {};
      if (isDryRun) {
        headers['X-Dry-Run'] = 'true';
      }

      const { data, error } = await supabase.functions.invoke(selectedWebhook, {
        body: parsedPayload,
        headers,
      });

      if (error) {
        setResult({ error: error.message });
        setResultStatus('error');
      } else {
        setResult(data);
        setResultStatus('success');
      }
    } catch (err: any) {
      setResult({ error: err.message });
      setResultStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadSample = () => {
    if (!selectedWebhook) return;
    const config = configs?.find(c => c.webhook_name === selectedWebhook);
    
    // Sample payloads based on webhook type
    const samples: Record<string, any> = {
      'facebook-webhook': {
        entry: [{
          changes: [{
            value: {
              leadgen_id: '123456789',
              form_id: '987654321',
              field_data: [
                { name: 'full_name', values: ['João Silva'] },
                { name: 'phone_number', values: ['+5511999999999'] },
                { name: 'email', values: ['joao@email.com'] }
              ]
            }
          }]
        }]
      },
      'create-lead-deal': {
        name: 'Maria Santos',
        phone: '+5511888888888',
        email: 'maria@email.com',
        source: 'website',
        market: 'BR'
      },
    };

    const sample = samples[selectedWebhook] || { message: 'Payload de teste', phone: '+5511999999999' };
    setPayload(JSON.stringify(sample, null, 2));
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Input Panel */}
      <div className="space-y-4">
        <div className="space-y-3">
          <div>
            <Label>Webhook</Label>
            <Select value={selectedWebhook} onValueChange={setSelectedWebhook}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione um webhook" />
              </SelectTrigger>
              <SelectContent>
                {configs?.map(c => (
                  <SelectItem key={c.webhook_name} value={c.webhook_name}>
                    {c.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Payload (JSON)</Label>
              <Button variant="link" size="sm" onClick={handleLoadSample} className="h-auto p-0 text-xs">
                Carregar exemplo
              </Button>
            </div>
            <Textarea
              value={payload}
              onChange={e => setPayload(e.target.value)}
              className="font-mono text-sm min-h-[250px]"
              placeholder='{"key": "value"}'
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch checked={isDryRun} onCheckedChange={setIsDryRun} id="dry-run" />
              <Label htmlFor="dry-run" className="cursor-pointer">
                Modo teste (Dry Run)
              </Label>
            </div>
            {!isDryRun && (
              <Badge variant="destructive" className="text-xs">
                ⚠ Criará entidades reais
              </Badge>
            )}
          </div>

          <Button onClick={handleSend} disabled={isLoading || !selectedWebhook} className="w-full">
            {isLoading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enviando...</>
            ) : (
              <><Play className="h-4 w-4 mr-2" /> Enviar Teste</>
            )}
          </Button>
        </div>
      </div>

      {/* Result Panel */}
      <Card className="border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            Resultado
            {resultStatus && (
              <Badge variant={resultStatus === 'success' ? 'default' : 'destructive'}>
                {resultStatus === 'success' ? 'Sucesso' : 'Erro'}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {result ? (
            <pre className="bg-muted rounded-lg p-4 text-xs overflow-auto max-h-[400px] whitespace-pre-wrap">
              {JSON.stringify(result, null, 2)}
            </pre>
          ) : (
            <p className="text-muted-foreground text-sm text-center py-12">
              Envie um teste para ver o resultado aqui
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
